import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import type { RequestItem, TaskDetailResponse } from "../../api/requests";
import {
  addCanvasPagesToPdf,
  applyPdfLightTheme,
  collectTableRowBreakPoints,
  loadPdfLogo,
  PDF_EXPORT_COLORS,
  PDF_HTML2CANVAS_SCALE,
} from "../../utils/pdfReportExport";

const sanitizeFilePart = (value?: string | null): string => {
  const cleaned = (value ?? "")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, " ")
    .replace(/\.+$/g, "")
    .slice(0, 80);
  return cleaned || "—";
};

const formatEndDateForFile = (iso?: string | null): string => {
  if (!iso) return "без-даты";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "без-даты";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear());
  return `${day}.${month}.${year}`;
};

const buildReportFileName = (data: RequestItem, taskDetail: TaskDetailResponse): string => {
  const company = sanitizeFilePart(data.companyName);
  const inn = sanitizeFilePart(data.inn);
  const endDate = formatEndDateForFile(taskDetail.endDateTime);
  return `${company}_${inn}_${endDate}.pdf`;
};

const downloadPdfBlob = (doc: jsPDF, fileName: string): void => {
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const exportRequestDetailPdf = async ({
  data,
  taskDetail,
  pdfContent,
}: {
  data: RequestItem;
  taskDetail: TaskDetailResponse;
  pdfContent: HTMLElement;
}): Promise<void> => {
  const logo = await loadPdfLogo();
  const exportRoot = document.createElement("div");
  exportRoot.style.position = "fixed";
  exportRoot.style.left = "-99999px";
  exportRoot.style.top = "0";
  exportRoot.style.width = "1200px";
  exportRoot.style.background = PDF_EXPORT_COLORS.bg;
  exportRoot.style.padding = "24px";
  exportRoot.style.color = PDF_EXPORT_COLORS.text;
  exportRoot.style.fontFamily = "Arial, sans-serif";
  exportRoot.style.boxSizing = "border-box";
  exportRoot.style.overflow = "visible";

  const brandRow = document.createElement("div");
  brandRow.style.display = "flex";
  brandRow.style.alignItems = "center";
  brandRow.style.gap = "16px";
  brandRow.style.marginBottom = "20px";
  brandRow.appendChild(logo);

  const brandText = document.createElement("span");
  brandText.textContent = "I N A R I";
  brandText.style.fontSize = "20px";
  brandText.style.letterSpacing = "4px";
  brandText.style.color = PDF_EXPORT_COLORS.text;
  brandRow.appendChild(brandText);
  exportRoot.appendChild(brandRow);

  const title = document.createElement("h2");
  title.textContent = "Отчет по заявке";
  title.style.margin = "0 0 16px";
  title.style.fontSize = "20px";
  title.style.fontWeight = "600";
  title.style.color = PDF_EXPORT_COLORS.text;
  exportRoot.appendChild(title);

  const appendMetaLine = (text: string) => {
    const line = document.createElement("div");
    line.textContent = text;
    line.style.marginBottom = "6px";
    line.style.color = PDF_EXPORT_COLORS.text;
    line.style.fontSize = "14px";
    exportRoot.appendChild(line);
  };

  appendMetaLine(`Компания: ${data.companyName}`);
  appendMetaLine(`ИНН: ${data.inn}`);
  appendMetaLine(`URL: ${data.url}`);
  if (taskDetail.endDateTime) {
    appendMetaLine(`Окончание: ${formatEndDateForFile(taskDetail.endDateTime)}`);
  }

  const reportClone = pdfContent.cloneNode(true) as HTMLElement;
  applyPdfLightTheme(reportClone);
  exportRoot.appendChild(reportClone);
  document.body.appendChild(exportRoot);

  try {
    const breakPoints = collectTableRowBreakPoints(exportRoot, PDF_HTML2CANVAS_SCALE);
    const canvas = await html2canvas(exportRoot, {
      scale: PDF_HTML2CANVAS_SCALE,
      backgroundColor: PDF_EXPORT_COLORS.bg,
      useCORS: true,
      logging: false,
      width: exportRoot.scrollWidth,
      height: exportRoot.scrollHeight,
      windowWidth: exportRoot.scrollWidth,
      windowHeight: exportRoot.scrollHeight,
    });
    const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4", compress: true });
    addCanvasPagesToPdf(doc, canvas, breakPoints);
    downloadPdfBlob(doc, buildReportFileName(data, taskDetail));
  } finally {
    if (exportRoot.parentNode) {
      exportRoot.parentNode.removeChild(exportRoot);
    }
  }
};
